
import { useState, useEffect, useCallback } from "react";
import { useNodesState, useEdgesState, addEdge, Node, Edge, Connection } from '@xyflow/react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Account, CustomerContact, RelationshipData, SerializedEdge } from "../types";

export const useOrgChartData = (selectedAccount: Account | null, user: any) => {
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);

  const serializeEdges = (edges: Edge[]): SerializedEdge[] => {
    return edges.map(edge => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle,
      targetHandle: edge.targetHandle,
      type: edge.type
    }));
  };

  const updateFlowNodes = useCallback((contacts: CustomerContact[], relationships: RelationshipData[]) => {
    const newNodes = contacts.map(contact => {
      const relationship = relationships.find(r => r.from_contact_id === contact.id);
      const position = relationship ? {
        x: relationship.position_x || 0,
        y: relationship.position_y || 0
      } : {
        x: Math.random() * 500,
        y: Math.random() * 400
      };
      return {
        id: contact.id.toString(),
        type: 'custom',
        data: {
          name: contact.customer_name,
          title: contact.title
        },
        position,
        style: {
          background: contact.support_status === 'Is Supporter' ? '#86efac' : contact.support_status === 'Is Against' ? '#fca5a5' : '#93c5fd',
          padding: '10px',
          borderRadius: '8px',
          width: 180,
          border: contact.id.toString() === selectedContactId ? '2px solid #ef4444' : '1px solid #EDEDED'
        }
      };
    });
    const allEdges = relationships.filter(r => r.edge_data).map(r => Array.isArray(r.edge_data) ? r.edge_data : [r.edge_data]).flat().map(edge => ({
      ...edge,
      id: edge.id || `e${edge.source}-${edge.target}`
    }));
    const uniqueEdges = Array.from(new Map(allEdges.map(edge => [edge.id, edge])).values());
    setNodes(newNodes);
    setEdges(uniqueEdges as Edge[]);
  }, [setNodes, setEdges, selectedContactId]);

  const loadContacts = async () => {
    if (!selectedAccount?.company_id || !user?.id) return null;
    
    // Get subordinate user IDs using the existing database function
    const { data: subordinateData, error: subordinateError } = await supabase
      .rpc('get_user_subordinates', { supervisor_uuid: user.id });
    
    if (subordinateError) {
      console.error('Error getting subordinates:', subordinateError);
    }
    
    // Create array of user IDs to include (current user + subordinates)
    const userIds = [user.id];
    if (subordinateData) {
      userIds.push(...subordinateData.map(sub => sub.subordinate_id));
    }
    
    const { data, error } = await supabase
      .from('customer_contacts')
      .select('*')
      .eq('company_id', selectedAccount.company_id)
      .in('user_id', userIds);
    
    if (error) {
      console.error('Error loading contacts:', error);
      return null;
    }
    
    const typedData = (data || []).map(contact => ({
      ...contact,
      support_status: (contact.support_status || 'Neutral') as CustomerContact['support_status']
    }));
    setContacts(typedData);
    return typedData;
  };

  const loadRelationships = useCallback(async () => {
    if (!selectedAccount?.company_id || !user?.id) return [];
    
    // Get subordinate user IDs using the existing database function
    const { data: subordinateData, error: subordinateError } = await supabase
      .rpc('get_user_subordinates', { supervisor_uuid: user.id });
    
    if (subordinateError) {
      console.error('Error getting subordinates:', subordinateError);
    }
    
    // Create array of user IDs to include (current user + subordinates)
    const userIds = [user.id];
    if (subordinateData) {
      userIds.push(...subordinateData.map(sub => sub.subordinate_id));
    }
    
    const { data, error } = await supabase
      .from('org_chart_relationships')
      .select('*')
      .eq('company_id', selectedAccount.company_id)
      .in('user_id', userIds);
    
    if (error) {
      console.error('Error loading relationships:', error);
      return [];
    }
    return data || [];
  }, [selectedAccount, user]);

  useEffect(() => {
    const loadData = async () => {
      const [contactsData, relationshipsData] = await Promise.all([loadContacts(), loadRelationships()]);
      if (contactsData && relationshipsData) {
        updateFlowNodes(contactsData, relationshipsData);
      }
    };
    if (selectedAccount?.company_id && user?.id) {
      loadData();
    }
  }, [selectedAccount, user, loadRelationships, updateFlowNodes]);

  return {
    contacts,
    setContacts,
    nodes,
    edges,
    setNodes,
    setEdges,
    selectedContactId,
    setSelectedContactId,
    onNodesChange,
    onEdgesChange,
    serializeEdges,
    updateFlowNodes,
    loadContacts,
    loadRelationships
  };
};
